/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../shared/components/button/Button';
import ChevronBackBtn from '../../shared/components/styledComponents/chevronBackBtn/ChevronBackBtn';
import { useHttpClient } from '../../shared/hooks/http-hook';
import { useUserData } from '../../shared/hooks/useUserData';
import './CallbackForm.css';

interface CallbackFormProps {
  onClose: () => void;
}

export function CallbackForm({ onClose }: CallbackFormProps): JSX.Element {
  const { t } = useTranslation();
  const { userData } = useUserData();
  const { isLoading, sendRequest } = useHttpClient();
  const [formData, setFormData] = useState({
    firstName: userData.fname || '',
    email: userData.email || '',
    phoneNumber: '',
    organisation: userData.clientName || '',
  });
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'error' | 'success' } | null>(null);

  const messageClassName = message
    ? `callback-component__message callback-component__message--${message.type}`
    : 'callback-component__message';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    const isFirstNameValid = formData.firstName.trim() !== '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = emailRegex.test(formData.email);
    const phoneRegex = /^\+?[0-9]+$/;
    const isPhoneValid = formData.phoneNumber === '' || phoneRegex.test(formData.phoneNumber);

    setNameError(!isFirstNameValid);
    setEmailError(!isEmailValid);
    setPhoneError(!isPhoneValid);

    if (!isFirstNameValid || !isEmailValid || !isPhoneValid) {
      setMessage({ text: 'Form validation failed, please update the entries.', type: 'error' });
      return;
    }

    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/cio/send-transactional`,
        'POST',
        JSON.stringify({
          transactional_message_id: 'pam_request_callback',
          identifiers: {
            id: userData.clientid,
          },
          to: 'counsellingteam@pamwellbeing.co.uk',
          from: 'support@championhealth.co.uk',
          subject: t('support.callbackForm.subject'),
          body: `This email has been triggered from Champion Health app in the support services section with following details. Do not reply.<br><br>
            Name: ${formData.firstName}${userData.fname && ` (${userData.fname})`}<br><br>
            Email: ${formData.email}<br><br>
            Phone number: ${formData.phoneNumber}<br><br>
            ${formData.organisation && `Organisation: ${formData.organisation}<br><br>`}
            Champion Health Client ID: ${userData.clientid}<br><br>`,
        })
      );
      setMessage({ text: 'Your callback request has been sent successfully!', type: 'success' });
      setFormData({
        firstName: '',
        email: '',
        phoneNumber: '',
        organisation: '',
      });
    } catch (err) {
      setMessage({
        text: t('support.callbackForm.error'),
        type: 'error',
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section className="callback-component" aria-labelledby="callback-form-title">
      <div className="btn-cont ">
        <ChevronBackBtn onClick={onClose} className="" />
      </div>
      <h2 className="callback-component__title" id="callback-form-title">
        {t('support.callbackForm.title')}
      </h2>
      <form className="callback-component__form" onSubmit={handleSubmit} noValidate>
        <div className="callback-component__input-container">
          <label className="callback-component__label" htmlFor="firstName">
            {t('R027')}
          </label>
          <input
            id="firstName"
            className="callback-component__input"
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder={t('R027')}
            required
            aria-required="true"
          />
          <div
            id="name-error"
            className="callback-component__error"
            role={nameError ? 'alert' : undefined}
          >
            {nameError && t('support.callbackForm.nameError')}
          </div>
        </div>

        <div className="callback-component__input-container">
          <label className="callback-component__label" htmlFor="email">
            {t('R023')}
          </label>
          <input
            id="email"
            className="callback-component__input"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={t('R023')}
            required
            aria-required="true"
            aria-invalid={emailError}
            aria-describedby="email-error"
          />
          <div
            id="email-error"
            className="callback-component__error"
            role={emailError ? 'alert' : undefined}
          >
            {emailError && t('R084')}
          </div>
        </div>

        <div className="callback-component__input-container">
          <label className="callback-component__label" htmlFor="phoneNumber">
            {t('support.callbackForm.phoneNumber')}
          </label>
          <input
            id="phoneNumber"
            className="callback-component__input"
            type="tel"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            placeholder={t('support.callbackForm.phoneNumber')}
            required
            aria-required="true"
            aria-invalid={phoneError}
            aria-describedby="phone-error"
          />
          <div
            id="phone-error"
            className="callback-component__error"
            role={phoneError ? 'alert' : undefined}
          >
            {phoneError && t('support.callbackForm.phoneError')}
          </div>
        </div>
        <div className="callback-component__input-container">
          <label className="callback-component__label" htmlFor="organisation">
            {t('support.callbackForm.organisation')}
          </label>
          <input
            id="organisation"
            className="callback-component__input"
            type="text"
            name="organisation"
            value={formData.organisation}
            onChange={handleChange}
            placeholder={t('support.callbackForm.organisation')}
          />
        </div>
        <div className={messageClassName} role="alert" aria-hidden={!message}>
          {message?.text}
        </div>
        <Button
          className="callback-component__button"
          color="pink"
          isDisabled={isLoading}
          type="submit"
        >
          {isLoading
            ? t('support.callbackForm.loading')
            : t('support.callbackForm.requestCallback')}
        </Button>
      </form>
    </section>
  );
}
